/**
 * Minimal single-series charts (dataviz skill: one hue for a single series, no legend needed,
 * thin marks, hover tooltip via <title>). Colours come from CSS tokens so light/dark both work.
 */
import { useMemo } from "react";

export function Sparkline({ points, width = 160, height = 36 }: { points: { ts: number; value: number | null }[]; width?: number; height?: number }) {
  const d = useMemo(() => {
    const vals = points.map((p) => p.value).filter((v): v is number => v != null);
    if (!points.length) return { path: "", dots: [] as { x: number; y: number; miss: boolean; label: string }[] };
    const min = Math.min(...vals, 0);
    const max = Math.max(...vals, 1);
    const x = (i: number) => (points.length === 1 ? width / 2 : 4 + (i / (points.length - 1)) * (width - 8));
    const y = (v: number) => height - 4 - ((v - min) / (max - min || 1)) * (height - 8);
    let path = "";
    let pen = false;
    const dots: { x: number; y: number; miss: boolean; label: string }[] = [];
    points.forEach((p, i) => {
      if (p.value == null) {
        pen = false;
        dots.push({ x: x(i), y: height - 4, miss: true, label: `${new Date(p.ts).toLocaleTimeString()} · missed` });
        return;
      }
      path += `${pen ? "L" : "M"}${x(i).toFixed(1)},${y(p.value).toFixed(1)} `;
      pen = true;
      if (i === points.length - 1) dots.push({ x: x(i), y: y(p.value), miss: false, label: `${new Date(p.ts).toLocaleTimeString()} · ${p.value}` });
    });
    return { path, dots };
  }, [points, width, height]);
  return (
    <svg className="spark" width={width} height={height} role="img" aria-label="trend">
      <path d={d.path} />
      {d.dots.map((p, i) => (
        <circle key={i} cx={p.x} cy={p.y} r={p.miss ? 3 : 4} className={p.miss ? "miss" : ""}>
          <title>{p.label}</title>
        </circle>
      ))}
    </svg>
  );
}

export function Bars({ rows, max }: { rows: { label: string; value: number; title?: string }[]; max?: number }) {
  const m = max ?? Math.max(1, ...rows.map((r) => r.value));
  return (
    <div className="bars">
      {rows.map((r, i) => (
        <div key={i} className="lbl" style={{ display: "contents" }}>
          <div className="lbl" title={r.title ?? r.label}>
            <span>{r.label}</span>
            <div className="bar" style={{ width: `${Math.max(2, (r.value / m) * 100)}%` }} title={`${r.label}: ${r.value}`} />
          </div>
          <span className="mono muted">{r.value.toLocaleString()}</span>
        </div>
      ))}
    </div>
  );
}
